import { useState, type ReactNode } from "react";
import { Download } from "lucide-react";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import type {
  StaffFinding,
  StaffPart,
  StaffProof,
} from "@shared-core/data/artwork-staff-types";
import { useObjectUiAdapter } from "@shared-core/data/ObjectUiAdapterContext";
import { formatShortDateOrDash } from "@shared-core/domain/formatters";
import type { RecordView } from "@shared-core/domain/object-ui/contract";
import {
  noArtworkSentences,
  orderArtworkSectionOf,
  type SectionVersion,
} from "@shared-core/domain/order-artwork-section";
import { Button } from "../../primitives/button";
import { Card, CardContent } from "../../primitives/card";
import { cn } from "../../primitives/utils";
import { SupplyGuide } from "./SupplyGuide";

const short = formatShortDateOrDash;

/**
 * The Artwork section of an artwork record (artwork spec 15 §7; One C2 moved
 * it from the order's record to the version's own): for each version its
 * files per part, the findings with their sentences, each proof (the PDF to
 * view, who decided it and how) and the history, newest first. Read-only:
 * every action is a command in the record's header, or on a held check
 * warning's row. Where a version stands is the backend's: the Artwork step's
 * node (when the record carries the step) and the version's `status_text`;
 * nothing is recomputed here. Renders nothing while the record carries no
 * artwork. One-only: a custom section on `artwork_preparation.artwork`.
 */
export function OrderArtworkSection({ record }: { record: RecordView }) {
  const section = orderArtworkSectionOf(record);
  if (!section) return null;
  return (
    <Card data-section-id="order.artwork">
      <CardContent className="space-y-3 py-4">
        <div className="space-y-0.5">
          <h2 className="text-base font-semibold">Artwork</h2>
          {section.summary && (
            <p
              className="text-sm text-muted-foreground"
              data-testid="artwork-summary"
            >
              {section.summary}
            </p>
          )}
        </div>
        {!section.hasVersions ? (
          <p className="text-sm text-muted-foreground">
            {noArtworkSentences(section).join(". ") || "No artwork"}.
          </p>
        ) : (
          section.lines.map(({ line, versions }) =>
            versions.length === 0 ? (
              <p
                key={line.line_ref}
                className="border-t pt-3 text-sm text-muted-foreground"
              >
                <span className="tabular-nums">{line.line_no}</span>{" "}
                {line.none_text}
              </p>
            ) : (
              versions.map((entry) => (
                <VersionArtwork key={entry.version.version_id} entry={entry} />
              ))
            ),
          )
        )}
      </CardContent>
    </Card>
  );
}

/** The node's colour, from the step's own state and problem. */
function statusClass(entry: SectionVersion): string {
  if (entry.node?.problem === "late") return "text-red-700 dark:text-red-400";
  if (entry.node?.problem === "at_risk")
    return "text-amber-700 dark:text-amber-400";
  if (entry.node?.state === "done") return "text-green-700 dark:text-green-400";
  return "text-muted-foreground";
}

function VersionArtwork({ entry }: { entry: SectionVersion }) {
  const { version, label, lineName } = entry;
  return (
    <section
      aria-label={`Artwork of ${label}`}
      className="space-y-2 border-t pt-3 text-sm"
    >
      <div>
        <h3 className="font-medium">
          <span className="tabular-nums">{label}</span>
          {lineName && (
            <span className="font-normal text-muted-foreground">
              {" "}
              {lineName}
            </span>
          )}
        </h3>
        <p className={cn("text-xs", statusClass(entry))}>
          {version.status_text}
        </p>
      </div>
      <ul className="space-y-2">
        {version.parts.map((part) => (
          <PartFile key={part.part_key} part={part} />
        ))}
      </ul>
      {version.colours_unknown && (
        <p className="text-xs text-muted-foreground">
          Colours and stock not known: spot colours are kept, not converted.
        </p>
      )}
      {version.from_item && (
        <p className="text-xs text-muted-foreground">
          {version.from_item.label}
          {version.from_item.stop_reason &&
            ` · ${version.from_item.stop_reason}`}
        </p>
      )}
      {version.proofs.length > 0 && (
        <ul aria-label={`Proofs of ${label}`} className="space-y-2">
          {version.proofs.map((proof) => (
            <ProofItem key={proof.id} proof={proof} />
          ))}
        </ul>
      )}
      {version.history.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground">
            History of {label}
          </summary>
          <ol className="mt-1 space-y-1">
            {version.history.map((event, n) => (
              <li key={`${event.at}-${n}`}>
                <span className="tabular-nums text-muted-foreground">
                  {short(event.at)}
                </span>
                <span className="text-muted-foreground"> · </span>
                {event.text}
                {event.by && (
                  <span className="text-muted-foreground"> · {event.by}</span>
                )}
              </li>
            ))}
          </ol>
        </details>
      )}
    </section>
  );
}

function PartFile({ part }: { part: StaffPart }) {
  const file = part.file;
  // The sentence sits where a file is wanted: nothing supplied yet, or one that can't print.
  const wantsFile = file === null || part.verdict === "cant_print";
  return (
    <li className="space-y-1">
      <div className="flex flex-wrap items-center gap-x-2">
        {part.label && (
          <span className="font-medium text-muted-foreground">
            {part.label}
          </span>
        )}
        {file ? (
          <>
            <span className="break-all">{file.file_name}</span>
            <DownloadLink
              href={file.download_url}
              filename={file.file_name}
              label={`Download original: ${file.file_name}`}
            >
              Download original
            </DownloadLink>
          </>
        ) : (
          <span className="text-muted-foreground">No file yet</span>
        )}
      </div>
      {file && (
        <p className="text-xs text-muted-foreground">
          {file.channel_label} {short(file.uploaded_at)} by {file.uploaded_by}
          {file.reused_from_revision !== null &&
            ` · reused from Rev ${file.reused_from_revision}`}
          {file.shared_with.length > 0 &&
            ` · shared with ${file.shared_with.join(", ")}`}
        </p>
      )}
      {part.verdict_sentence && (
        <p className="text-xs">{part.verdict_sentence}</p>
      )}
      {part.findings.length > 0 && (
        <ul className="list-disc space-y-1 pl-5 text-xs">
          {part.findings.map((finding) => (
            <FindingItem key={finding.id} finding={finding} />
          ))}
        </ul>
      )}
      {wantsFile && (
        <SupplyGuide
          sentence={part.supply_sentence}
          notes={part.supply_notes}
        />
      )}
    </li>
  );
}

function FindingItem({ finding }: { finding: StaffFinding }) {
  return (
    <li>
      {finding.sentence ??
        "This check warning is held until someone decides it."}
      <span className="block text-muted-foreground">
        {finding.check_name}
        {finding.detail && ` · ${finding.detail}`}
      </span>
    </li>
  );
}

function ProofItem({ proof }: { proof: StaffProof }) {
  return (
    <li className="space-y-0.5">
      <div className="flex flex-wrap items-center gap-x-2">
        <span className="font-medium">{proof.label}</span>
        <span className="text-muted-foreground">
          of Rev {proof.of_revision} · {proof.status_line}
        </span>
        <DownloadLink
          href={proof.pdf_url}
          filename={`${proof.label}.pdf`}
          label={`View ${proof.label}`}
        >
          View {proof.label}
        </DownloadLink>
      </div>
      {proof.decided_by && (
        <p className="text-xs text-muted-foreground">
          Decided {short(proof.decided_at)} by {proof.decided_by}
        </p>
      )}
      {proof.how_approved && (
        <p className="text-xs text-muted-foreground">
          How approved: {proof.how_approved}
        </p>
      )}
      {proof.reason_label && (
        <p className="text-xs text-muted-foreground">
          {proof.reason_label}
          {proof.comment && `: “${proof.comment}”`}
        </p>
      )}
      {proof.accepted_findings.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Accepted: {proof.accepted_findings.map((f) => f.sentence).join(" ")}
        </p>
      )}
      {proof.reopen_refusal && (
        <p className="text-xs text-muted-foreground">{proof.reopen_refusal}</p>
      )}
    </li>
  );
}

/**
 * A file or a proof PDF at the API path the read carried, fetched with the
 * person's sign-in and saved: a plain link would reach the API without it.
 */
function DownloadLink({
  href,
  filename,
  label,
  children,
}: {
  href: string;
  filename: string;
  label: string;
  children: ReactNode;
}) {
  const adapter = useObjectUiAdapter();
  const capabilities = usePlatformCapabilities();
  const [hasFailed, setHasFailed] = useState(false);
  const save = async () => {
    setHasFailed(false);
    try {
      const { blob, filename: served } = await adapter.download(href);
      capabilities.downloadBlob(served || filename, blob);
    } catch {
      console.warn("object_ui.order_artwork.download_failed", { filename });
      setHasFailed(true);
    }
  };
  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        className="h-7 px-2"
        aria-label={label}
        onClick={() => void save()}
      >
        <Download aria-hidden className="size-3.5" />
        {children}
      </Button>
      {hasFailed && (
        <span role="alert" className="text-xs text-red-600">
          {filename} couldn't be downloaded.
        </span>
      )}
    </>
  );
}
